/**
 * Error handling utilities for the automatic scheduling feature
 */

export interface ErrorContext {
  operation: string;
  parameters?: any;
  timestamp: number;
  userAgent?: string;
}

export interface ErrorAnalysis {
  category: 'validation' | 'network' | 'timeout' | 'data' | 'performance' | 'system' | 'unknown';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userMessage: string;
  technicalMessage: string;
  suggestedActions: string[];
  shouldRetry: boolean;
  shouldShowFallback: boolean;
  retryDelay?: number;
}

/**
 * Analyze an error and provide structured information for handling
 */
export function analyzeError(error: Error | string, context?: ErrorContext): ErrorAnalysis {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();

  // Validation errors
  if (lowerMessage.includes('please') || 
      lowerMessage.includes('invalid') ||
      lowerMessage.includes('must be') ||
      lowerMessage.includes('required') ||
      lowerMessage.includes('select at least')) {
    return {
      category: 'validation',
      severity: 'low',
      userMessage: errorMessage,
      technicalMessage: errorMessage,
      suggestedActions: ['Review your input parameters', 'Check the highlighted fields'],
      shouldRetry: false,
      shouldShowFallback: false
    };
  }

  // Network errors
  if (lowerMessage.includes('network') ||
      lowerMessage.includes('fetch') ||
      lowerMessage.includes('connection') ||
      lowerMessage.includes('networkerror') ||
      lowerMessage.includes('failed to fetch')) {
    return {
      category: 'network',
      severity: 'medium',
      userMessage: 'Network connection failed. Please check your internet connection and try again.',
      technicalMessage: errorMessage,
      suggestedActions: [
        'Check your internet connection',
        'Try again in a few moments',
        'Contact support if the problem persists'
      ],
      shouldRetry: true,
      shouldShowFallback: true,
      retryDelay: 2000
    };
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || 
      lowerMessage.includes('timed out') ||
      lowerMessage.includes('took too long')) {
    return {
      category: 'timeout',
      severity: 'medium',
      userMessage: 'The operation took too long to complete. This usually happens when search parameters are too broad.',
      technicalMessage: errorMessage,
      suggestedActions: [
        'Select fewer weeks or days',
        'Reduce the time range',
        'Select fewer rooms',
        'Try the "Optimized Parameters" option'
      ],
      shouldRetry: false,
      shouldShowFallback: true
    };
  }

  // Data errors
  if (lowerMessage.includes('data is') ||
      lowerMessage.includes('not available') ||
      lowerMessage.includes('not loaded') ||
      lowerMessage.includes('refresh')) {
    return {
      category: 'data',
      severity: 'medium',
      userMessage: 'Required data is not available. This may be a temporary loading issue.',
      technicalMessage: errorMessage,
      suggestedActions: [
        'Refresh the page',
        'Wait a moment and try again',
        'Contact support if data is consistently missing'
      ],
      shouldRetry: true,
      shouldShowFallback: true,
      retryDelay: 1000
    };
  }

  // Performance errors
  if (lowerMessage.includes('combinations') ||
      lowerMessage.includes('too broad') ||
      lowerMessage.includes('performance') ||
      lowerMessage.includes('limit')) {
    return {
      category: 'performance',
      severity: 'medium',
      userMessage: 'Search parameters are too broad, which would take too long to process.',
      technicalMessage: errorMessage,
      suggestedActions: [
        'Select fewer weeks (try 1-2 weeks)',
        'Select fewer days (try 2-3 days)',
        'Reduce time range (try 6-8 hours)',
        'Select specific rooms instead of all rooms'
      ],
      shouldRetry: false,
      shouldShowFallback: true
    };
  }

  // System errors
  if (lowerMessage.includes('system') ||
      lowerMessage.includes('internal') ||
      lowerMessage.includes('server') ||
      lowerMessage.includes('database')) {
    return {
      category: 'system',
      severity: 'high',
      userMessage: 'A system error occurred. Please try again or contact support if the problem persists.',
      technicalMessage: errorMessage,
      suggestedActions: [
        'Try again in a few moments',
        'Refresh the page',
        'Contact support with error details'
      ],
      shouldRetry: true,
      shouldShowFallback: true,
      retryDelay: 5000
    };
  }

  // Unknown errors
  return {
    category: 'unknown',
    severity: 'medium',
    userMessage: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
    technicalMessage: errorMessage,
    suggestedActions: [
      'Try again with different parameters',
      'Refresh the page',
      'Contact support if the problem continues'
    ],
    shouldRetry: true,
    shouldShowFallback: true,
    retryDelay: 3000
  };
}

/**
 * Format error message for display to users
 */
export function formatErrorMessage(analysis: ErrorAnalysis, includeActions: boolean = true): string {
  let message = analysis.userMessage;

  if (includeActions && analysis.suggestedActions.length > 0) {
    message += '\n\nSuggested actions:\n';
    message += analysis.suggestedActions.map(action => `• ${action}`).join('\n');
  }

  return message;
}

/**
 * Log error with context for debugging
 */
export function logError(error: Error | string, context?: ErrorContext, analysis?: ErrorAnalysis): void {
  const timestamp = new Date().toISOString();
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  console.error(`[ErrorHandler] ${timestamp}`, {
    error: errorMessage,
    context,
    analysis,
    stack: error instanceof Error ? error.stack : undefined
  });

  // In production, you might want to send this to an error tracking service
  if (process.env.NODE_ENV === 'production' && analysis?.severity === 'critical') {
    // Example: Send to error tracking service
    // errorTrackingService.captureError(error, context);
  }
}

/**
 * Create a retry function with exponential backoff
 */
export function createRetryFunction(
  originalFunction: () => Promise<any>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): () => Promise<any> {
  return async () => {
    let lastError: Error;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await originalFunction();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        if (attempt === maxRetries) {
          throw lastError;
        }

        const analysis = analyzeError(lastError);
        if (!analysis.shouldRetry) {
          throw lastError;
        }

        const delay = Math.min(baseDelay * Math.pow(2, attempt), 10000);
        console.log(`[ErrorHandler] Retry attempt ${attempt + 1}/${maxRetries} in ${delay}ms`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError!;
  };
}

/**
 * Validate that required data is available and properly formatted
 */
export function validateRequiredData(data: {
  teachers?: any[];
  groups?: any[];
  subjects?: any[];
  rooms?: any[];
  scheduleItems?: any[];
}): string | null {
  const { teachers, groups, subjects, rooms, scheduleItems } = data;

  if (!Array.isArray(teachers) || teachers.length === 0) {
    return 'Teacher data is not available. Please refresh the page and try again.';
  }

  if (!Array.isArray(groups) || groups.length === 0) {
    return 'Group data is not available. Please refresh the page and try again.';
  }

  if (!Array.isArray(subjects) || subjects.length === 0) {
    return 'Subject data is not available. Please refresh the page and try again.';
  }

  if (!Array.isArray(rooms) || rooms.length === 0) {
    return 'Room data is not available. Please refresh the page and try again.';
  }

  if (!Array.isArray(scheduleItems)) {
    return 'Schedule data is not available. Please refresh the page and try again.';
  }

  // Validate data structure
  const invalidTeachers = teachers.filter(t => !t.id || !t.name);
  if (invalidTeachers.length > 0) {
    return `Invalid teacher data detected (${invalidTeachers.length} entries). Please refresh the page.`;
  }

  const invalidGroups = groups.filter(g => !g.id || !g.name);
  if (invalidGroups.length > 0) {
    return `Invalid group data detected (${invalidGroups.length} entries). Please refresh the page.`;
  }

  const invalidSubjects = subjects.filter(s => !s.id || !s.name);
  if (invalidSubjects.length > 0) {
    return `Invalid subject data detected (${invalidSubjects.length} entries). Please refresh the page.`;
  }

  const invalidRooms = rooms.filter(r => !r.name);
  if (invalidRooms.length > 0) {
    return `Invalid room data detected (${invalidRooms.length} entries). Please refresh the page.`;
  }

  return null;
}